import React from 'react';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '../../shadcn/components/ui/button';
import { Card, CardContent } from '../../shadcn/components/ui/card';

export interface ErrorCardProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  onBack?: () => void;
  retryButtonText?: string;
  backButtonText?: string;
  showRetryButton?: boolean;
  showBackButton?: boolean;
  variant?: 'destructive' | 'warning' | 'info';
  className?: string;
  isRetrying?: boolean;
}

export const ErrorCard: React.FC<ErrorCardProps> = ({
  title = 'Something went wrong',
  message,
  onRetry,
  onBack,
  retryButtonText = 'Try Again',
  backButtonText = 'Go Back',
  showRetryButton = true,
  showBackButton = false,
  variant = 'destructive',
  className = '',
  isRetrying = false,
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'warning':
        return {
          cardBg: 'bg-yellow-50',
          border: 'border-yellow-200',
          iconColor: 'text-yellow-600',
          titleColor: 'text-yellow-900',
          messageColor: 'text-yellow-700',
        };
      case 'info':
        return {
          cardBg: 'bg-blue-50',
          border: 'border-blue-200',
          iconColor: 'text-blue-600',
          titleColor: 'text-blue-900',
          messageColor: 'text-blue-700',
        };
      case 'destructive':
      default:
        return {
          cardBg: 'bg-red-50',
          border: 'border-red-200',
          iconColor: 'text-red-600',
          titleColor: 'text-red-900',
          messageColor: 'text-red-700',
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <Card className={`shadow-sm ${styles.cardBg} ${styles.border} ${className}`}>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 ${styles.iconColor}`}>
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-lg font-semibold ${styles.titleColor} mb-2`}>
              {title}
            </h3>
            <p className={`text-sm ${styles.messageColor} mb-4 leading-relaxed`}>
              {message}
            </p>
            <div className="flex items-center gap-3">
              {showRetryButton && onRetry && (
                <Button
                  onClick={onRetry}
                  disabled={isRetrying}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <RefreshCw 
                    className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} 
                  />
                  {isRetrying ? 'Retrying...' : retryButtonText}
                </Button>
              )}
              {showBackButton && onBack && (
                <Button
                  onClick={onBack}
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {backButtonText}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 